import React, { useCallback, useEffect, useState } from 'react';
import { NudgeSpotlight } from '@atlassiansox/nudge-tooltip';
import Button, { IconButton } from '@atlaskit/button/new';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock-filled';
import LockUnlockedIcon from '@atlaskit/icon/core/migration/lock-unlocked--unlock';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import type { GlyphProps } from '@atlaskit/icon/types';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useSeenStatus } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/controllers/seen-status/index.tsx';
import type {
	RequestTypeIssueSecurityStatus,
	RequestTypeRestrictionStatus,
} from '@atlassian/jira-servicedesk-ticket-types-store/src/common/types.tsx';
import { createOnboardingSpotlight } from '@atlassian/jira-software-onboarding-nudges--next/src/common/ui/onboarding-spotlight/index.tsx';
import { Link } from '@atlassian/react-resource-router';
import {
	REQUEST_TYPE_ISSUE_SECURITY_CHANGEBOARDING_USER_PROPERTY_KEY,
	REQUEST_TYPE_SECURITY_ONBOARDING_USER_PROPERTY_KEY,
	REQUEST_TYPE_SECURITY_ONBOARDING_V2_USER_PROPERTY_KEY,
	packageName,
	teamName,
} from '../../constants.tsx';
import { isMisconfigured } from '../../controllers/issue-security/utils.tsx';
import { ConfigModalIllustration } from './config-modal-illustration/index.tsx';
import { ConfigModal } from './config-modal/index.tsx';
import type { SupportedFeature } from './config-modal/types.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const RedLockFilledIcon = (props: GlyphProps) => {
	const { label } = props;
	return (
		<LockLockedIcon
			color={token('color.icon.accent.red')}
			LEGACY_size="medium"
			label={label}
			testId="servicedesk-request-type-security.ui.config-button.lock-icon"
		/>
	);
};

const UnlockIcon = (props: GlyphProps) => {
	const { label } = props;
	return (
		<LockUnlockedIcon
			color={token('color.text')}
			LEGACY_size="medium"
			label={label}
			testId="servicedesk-request-type-security.ui.config-button.unlock-icon"
		/>
	);
};

const RedErrorIcon = (props: GlyphProps) => {
	const { label } = props;
	return (
		<ErrorIcon
			color={token('color.icon.danger')}
			LEGACY_size="small"
			label={label}
			testId="servicedesk-request-type-security.ui.config-button.error-icon"
		/>
	);
};

const ConfigButtonBase = ({
	projectId,
	projectKey,
	requestType,
	appearance,
	skipOnboarding,
	features,
	showText,
}: Props) => {
	const { formatMessage } = useIntl();
	const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
	const [restrictionStatus, setRestrictionStatus] = useState(requestType.restrictionStatus);
	const [issueSecurityStatus, setIssueSecurityStatus] = useState(requestType.issueSecurityStatus);

	const [{ isSeen: hasSeenOldOnboarding }] = useSeenStatus({
		userPropertyKey: REQUEST_TYPE_SECURITY_ONBOARDING_USER_PROPERTY_KEY,
		isSeenValue: true,
		packageName,
		teamName,
		skipOnboarding,
	});

	const [{ isSeen: hasSeenOnboarding }, { markAsSeen: markOnboardingAsSeen }] = useSeenStatus({
		userPropertyKey: REQUEST_TYPE_SECURITY_ONBOARDING_V2_USER_PROPERTY_KEY,
		isSeenValue: true,
		packageName,
		teamName,
		skipOnboarding,
	});

	const [
		{ isSeen: hasSeenIssueSecurityChangeboarding },
		{ markAsSeen: markIssueSecurityChangeboardingAsSeen },
	] = useSeenStatus({
		userPropertyKey: REQUEST_TYPE_ISSUE_SECURITY_CHANGEBOARDING_USER_PROPERTY_KEY,
		isSeenValue: true,
		packageName,
		teamName,
		skipOnboarding,
	});

	const issueSecurityChangeboardingActive =
		!hasSeenIssueSecurityChangeboarding && requestType.issueSecurityStatus !== 'INACTIVE';

	const [defaultFeature, setDefaultFeature] = useState<SupportedFeature>();
	useEffect(() => {
		if ((!hasSeenOnboarding && hasSeenOldOnboarding) || issueSecurityChangeboardingActive) {
			setDefaultFeature('ISSUE_SECURITY');
		}
	}, [hasSeenOldOnboarding, hasSeenOnboarding, issueSecurityChangeboardingActive]);

	const openConfigModal = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		if (!skipOnboarding) {
			if (issueSecurityChangeboardingActive) {
				markIssueSecurityChangeboardingAsSeen();
			}
			markOnboardingAsSeen();
		}
		fireUIAnalytics(analyticsEvent, 'requestTypeRestriction');
		setIsConfigModalOpen(true);
	};

	const close = useCallback(
		(
			newRestrictionStatus?: RequestTypeRestrictionStatus,
			newIssueSecurityStatus?: RequestTypeIssueSecurityStatus,
		) => {
			if (newRestrictionStatus) {
				setRestrictionStatus(newRestrictionStatus);
			}
			if (newIssueSecurityStatus) {
				if (newIssueSecurityStatus === 'ACTIVE') {
					markIssueSecurityChangeboardingAsSeen();
				}
				setIssueSecurityStatus(newIssueSecurityStatus);
			}
			if (requestType.onSecurityStatusChange) {
				requestType.onSecurityStatusChange(newRestrictionStatus, newIssueSecurityStatus);
			}
			setIsConfigModalOpen(false);
			setDefaultFeature(undefined);
		},
		[markIssueSecurityChangeboardingAsSeen, requestType],
	);

	useEffect(() => {
		setRestrictionStatus(requestType.restrictionStatus);
	}, [requestType.restrictionStatus]);
	useEffect(() => {
		setIssueSecurityStatus(requestType.issueSecurityStatus);
	}, [requestType.issueSecurityStatus]);

	const hasNoRestrictions = restrictionStatus === 'OPEN' && issueSecurityStatus === 'INACTIVE';

	const renderNudge = () => {
		const headingMessage = fg('itsm-issue-renaming')
			? formatMessage(
					issueSecurityChangeboardingActive
						? messages.changeboardingNudgeHeadingIssueTermRefresh
						: messages.onboardingNudgeHeading,
				)
			: formatMessage(
					issueSecurityChangeboardingActive
						? messages.changeboardingNudgeHeading
						: messages.onboardingNudgeHeading,
				);

		const contentMessageTermRefresh = issueSecurityChangeboardingActive
			? messages.changeboardingNudgeContentIssueTermRefresh
			: messages.onboardingNudgeContentIssueTermRefresh;
		const contentMessage = issueSecurityChangeboardingActive
			? messages.changeboardingNudgeContent
			: messages.onboardingNudgeContent;

		return createOnboardingSpotlight({
			actions: [
				{
					onClick: () => {
						if (issueSecurityChangeboardingActive) {
							markIssueSecurityChangeboardingAsSeen();
						}
						markOnboardingAsSeen();
					},
					content: formatMessage(messages.onboardingNudgeDismissButton),
					appearance: 'subtle',
				},
				{
					onClick: openConfigModal,
					content: formatMessage(messages.onboardingNudgeShowMeButton),
					appearance: 'primary',
				},
			],

			image: <ConfigModalIllustration alt={headingMessage} />,
			headingMessage,
			contentMessage: (
				<FormattedMessage
					{...(fg('itsm-issue-renaming') ? contentMessageTermRefresh : contentMessage)}
					values={{
						b: (chunks) => <b>{chunks}</b>,
						link: (chunks) => (
							<Link
								href="https://support.atlassian.com/jira-service-management-cloud/docs/create-security-levels-for-issues"
								target="_blank"
							>
								{chunks}
							</Link>
						),
					}}
				/>
			),
		});
	};

	const button = (
		<Inline>
			<NudgeSpotlight
				hidden={hasSeenOnboarding && !issueSecurityChangeboardingActive}
				setHidden={() => {
					/* state handled externally */
				}}
				spotlightCard={renderNudge()}
				position="bottom-end"
				showSpotlightCardOnFirstRender={false}
				nudgeTestId="servicedesk-request-type-security.onboarding.nudge"
			>
				<Tooltip
					content={
						fg('itsm-issue-renaming')
							? formatMessage(
									hasNoRestrictions
										? messages.openTooltipV2IssueTermRefresh
										: messages.restrictedTooltipV2IssueTermRefresh,
								)
							: formatMessage(
									hasNoRestrictions ? messages.openTooltipV2 : messages.restrictedTooltipV2,
								)
					}
				>
					{showText ? (
						<Button
							appearance={appearance}
							onClick={openConfigModal}
							testId="servicedesk-request-type-security.ui.config-button"
							iconBefore={hasNoRestrictions ? UnlockIcon : RedLockFilledIcon}
							iconAfter={isMisconfigured(issueSecurityStatus) ? RedErrorIcon : undefined}
							aria-label={formatMessage(
								hasNoRestrictions ? messages.openV2 : messages.restrictedV2,
							)}
						>
							{formatMessage(messages.restrictions)}
						</Button>
					) : (
						<IconButton
							appearance={appearance}
							onClick={openConfigModal}
							testId="servicedesk-request-type-security.ui.config-button"
							icon={hasNoRestrictions ? UnlockIcon : RedLockFilledIcon}
							label={formatMessage(hasNoRestrictions ? messages.openV2 : messages.restrictedV2)}
						/>
					)}
				</Tooltip>
			</NudgeSpotlight>
		</Inline>
	);

	return (
		<ContextualAnalyticsData
			attributes={{
				projectId,
				requestTypeExternalId: requestType.externalId,
				restrictionStatus,
				issueSecurityStatus,
				supportRequestTypePermission: features.includes('REQUEST_TYPE_PERMISSIONS'),
				supportIssueSecurity: features.includes('ISSUE_SECURITY'),
			}}
		>
			<ConfigModal
				isOpen={isConfigModalOpen}
				onClose={close}
				projectId={projectId}
				projectKey={projectKey}
				requestTypeExternalId={requestType.externalId}
				isEmailRequestType={requestType.usedByEmailSettings}
				features={features}
				defaultFeature={defaultFeature}
			/>
			{button}
		</ContextualAnalyticsData>
	);
};

export const ConfigButton = (props: Props) => (
	<JSErrorBoundary
		id="configButton"
		packageName={packageName}
		teamName={teamName}
		sendToPrivacyUnsafeSplunk
		fallback="flag"
	>
		<ConfigButtonBase {...props} />
	</JSErrorBoundary>
);
